import { useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { Send, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { toast } from "sonner";

export default function AiChat({ role, title, suggestions }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const ask = async (txt) => {
    const t = (txt ?? input).trim();
    if (!t) return;
    setMsgs((m) => [...m, { who: "me", body: t }, { who: "ai", body: "" }]);
    setInput("");
    setBusy(true);
    try {
      const tok = localStorage.getItem("trvpro_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ message: t }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || "AI error");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        acc += chunk;
        setMsgs((m) => {
          const c = [...m];
          c[c.length - 1] = { who: "ai", body: acc };
          return c;
        });
        endRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    } catch (e) {
      toast.error("L'IA est temporairement indisponible.");
      setMsgs((m) => m.slice(0, -2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell role={role} title={title}>
      {msgs.length === 0 && (
        <div className="text-center mt-4 mb-6">
          <div className="h-14 w-14 rounded-2xl bg-brand-light text-brand mx-auto flex items-center justify-center mb-3"><Sparkles className="w-7 h-7" /></div>
          <h2 className="font-heading font-bold text-xl text-ink">{role === "pro" ? "Votre coach business" : "Votre assistant travaux"}</h2>
          <p className="text-sm text-slate2 mt-1.5 leading-relaxed px-4">
            {role === "pro"
              ? "Posez-moi vos questions pour booster votre activité, vos devis, votre conversion."
              : "Demandez une estimation, un conseil ou une recommandation pour votre projet."}
          </p>
        </div>
      )}

      <div className="space-y-2.5 mb-4">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.who === "me" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.who === "me" ? "bg-pro text-white rounded-br-md" : "bg-white border border-border text-ink rounded-bl-md"}`}>
              {m.body || <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" />
                <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
                <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
              </span>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {msgs.length === 0 && (
        <div className="flex flex-col gap-2 mb-4">
          {suggestions.map((s) => (
            <button key={s} onClick={() => ask(s)} className="text-left bg-white border border-border rounded-2xl p-3 tap-scale text-sm text-ink" data-testid={`ai-suggestion-${s.slice(0, 16)}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="fixed bottom-20 left-1/2 -translate-x-1/2 w-full max-w-[480px] px-5">
        <div className="bg-white border border-border rounded-full p-1.5 flex items-center gap-2 shadow-lg">
          <Input
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="Posez votre question…"
            className="border-0 focus-visible:ring-0 h-10"
            data-testid="ai-input"
          />
          <Button onClick={() => ask()} disabled={busy} className="rounded-full h-10 w-10 p-0 bg-brand hover:bg-brand-hover shrink-0" data-testid="ai-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
